import type { AppContext } from "../app/app";
import { CANONICAL_QUALITIES, canonicalByKey } from "../data/canonical";
import { PACKS, SOFT_DIMENSION_CAP, mergePacks, type Pack } from "../data/packs";
import { proposeDimensions, suggestCanonical } from "../llm/partner";
import type { ProposedDimension } from "../llm/prompts";
import { el } from "./dom";
import { requireProcessingConsent } from "./privacy";

// A picklist over the canonical backbone, plus an explicit subjective-only
// option. The canonical key is plumbing for a future network phase; the user's
// own label and definition stay the primary thing shown elsewhere.
export function canonicalSelect(selected: string | null): HTMLSelectElement {
  const sel = el("select", { class: "input select" }) as HTMLSelectElement;
  sel.append(el("option", { value: "", text: "None (keep private / subjective-only)" }));
  for (const q of CANONICAL_QUALITIES) {
    const label = q.relationship ? `${q.name} (relationship, not a quality)` : q.name;
    sel.append(el("option", { value: q.key, text: label }));
  }
  sel.value = selected ?? "";
  return sel;
}

// Read the chosen key back as a backbone key or null (subjective-only).
export function readCanonical(sel: HTMLSelectElement): string | null {
  return sel.value === "" ? null : sel.value;
}

// Clamp a list of dimension names to roughly two lines: keep names until a
// character budget is reached, then report how many remain as "+N more". Always
// keeps at least one. Pure, so the clamp behaviour is testable headlessly.
export function clampDimensionNames(
  names: string[],
  maxChars = 32,
): { shown: string[]; more: number } {
  const shown: string[] = [];
  let used = 0;
  for (const n of names) {
    const add = (shown.length ? 3 : 0) + n.length; // 3 for the " · " separator
    if (shown.length > 0 && used + add > maxChars) break;
    used += add;
    shown.push(n);
  }
  return { shown, more: names.length - shown.length };
}

// Only one dimension popover is open at a time, app-wide.
let closeActiveDimPopover: (() => void) | null = null;

// The "+N more" control: a focusable button that reveals the pack's full
// dimension list (with definitions) in a floating popover. Clicks never reach the
// card, so opening it does not toggle pack selection.
function moreControl(pack: Pack, more: number): HTMLButtonElement {
  const button = el("button", {
    class: "pack-card__more",
    type: "button",
    text: `+${more} more`,
    attrs: {
      "aria-expanded": "false",
      "aria-haspopup": "dialog",
      "aria-label": `Show all dimensions in ${pack.label}`,
    },
  }) as HTMLButtonElement;
  const open = () => {
    if (button.getAttribute("aria-expanded") === "true") {
      closeActiveDimPopover?.();
      return;
    }
    closeActiveDimPopover?.();
    openDimPopover(button, pack);
  };
  button.addEventListener("click", (e) => {
    e.stopPropagation();
    open();
  });
  button.addEventListener("keydown", (e) => {
    const k = (e as KeyboardEvent).key;
    if (k === "Enter" || k === " ") {
      e.preventDefault();
      e.stopPropagation();
      open();
    }
  });
  return button;
}

function openDimPopover(button: HTMLButtonElement, pack: Pack): void {
  const pop = el("div", {
    class: "dim-popover",
    attrs: { role: "dialog", "aria-label": `Dimensions in ${pack.label}`, tabindex: "-1" },
  });
  const list = el("ul", { class: "dim-popover__list" });
  for (const d of pack.dimensions) {
    list.append(
      el(
        "li",
        { class: "dim-popover__item" },
        el("span", { class: "dim-popover__name", text: d.name }),
        d.definition ? el("span", { class: "dim-popover__def", text: d.definition }) : null,
      ),
    );
  }
  pop.append(list);
  pop.addEventListener("click", (e) => e.stopPropagation());
  document.body.append(pop);
  positionPopover(pop, button);
  button.setAttribute("aria-expanded", "true");
  pop.focus();

  const close = () => {
    document.removeEventListener("click", onDocClick);
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("scroll", onScroll, true);
    window.removeEventListener("resize", onScroll);
    pop.remove();
    button.setAttribute("aria-expanded", "false");
    closeActiveDimPopover = null;
    button.focus();
  };
  const onDocClick = (e: MouseEvent) => {
    if (e.target !== button && !pop.contains(e.target as Node)) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };
  const onScroll = () => close();
  // The opening click calls stopPropagation, so it never reaches the document;
  // the listener can be added immediately without catching it.
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onKey);
  window.addEventListener("scroll", onScroll, true);
  window.addEventListener("resize", onScroll);
  closeActiveDimPopover = close;
}

// Anchor below the control by default; flip above and shift inward to stay within
// the viewport. Fixed positioning, so the popover floats over the layout and does
// not affect card or row height.
function positionPopover(pop: HTMLElement, anchor: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  const margin = 8;
  pop.style.position = "fixed";
  pop.style.visibility = "hidden";
  const pw = pop.offsetWidth;
  const ph = pop.offsetHeight;
  let top = r.bottom + 6;
  if (top + ph > window.innerHeight - margin) {
    const above = r.top - ph - 6;
    top = above >= margin ? above : Math.max(margin, window.innerHeight - margin - ph);
  }
  let left = r.left;
  if (left + pw > window.innerWidth - margin) left = window.innerWidth - margin - pw;
  if (left < margin) left = margin;
  pop.style.top = `${top}px`;
  pop.style.left = `${left}px`;
  pop.style.visibility = "visible";
}

// Pack chooser: pick one OR MORE purpose packs; the union of their dimensions is
// seeded. Equal-height, description-led cards (the dimension list is clamped so
// it never makes a card taller than its row siblings). A live preview shows the
// merged count, a soft-cap warning, and a note where two packs map to the same
// backbone quality. opts.primaryLabel sets the action button text from the count;
// opts.onGeneric adds a secondary "just use the generic set". The caller decides
// what applying means (seed on first run, replace in settings).
export function renderPackChooser(
  onApply: (packs: Pack[]) => void,
  opts: { primaryLabel?: (count: number) => string; onGeneric?: () => void } = {},
): HTMLElement {
  const selected = new Set<string>();
  const grid = el("div", { class: "pack-grid" });
  const preview = el("div", { class: "pack-preview" });
  const label = opts.primaryLabel ?? ((n) => (n === 0 ? "Use selected sets" : `Use ${n} set${n === 1 ? "" : "s"}`));
  const apply = el("button", { class: "btn btn--primary", text: label(0) });
  apply.disabled = true;

  const refresh = () => {
    apply.disabled = selected.size === 0;
    apply.textContent = label(selected.size);
    preview.replaceChildren();
    if (selected.size === 0) {
      preview.append(el("p", { class: "lede", text: "Select one or more sets to begin." }));
      return;
    }
    const merge = mergePacks(PACKS.filter((p) => selected.has(p.key)));
    preview.append(
      el("p", { class: "pack-preview__count num", text: `${merge.dimensions.length} qualities to tag against` }),
    );
    if (merge.overSoftCap) {
      preview.append(
        el("p", {
          class: "pack-preview__warn",
          text:
            `That is a lot to keep up. Past ${SOFT_DIMENSION_CAP} qualities, ` +
            "tagging gets heavy. Consider fewer sets, or trim later in Settings.",
        }),
      );
    }
    for (const g of merge.sharedKeyGroups) {
      const canon = canonicalByKey(g.canonical_key);
      preview.append(
        el("p", {
          class: "pack-preview__note",
          text:
            `Both map to ${canon?.name ?? g.canonical_key}: ${g.names.join(", ")}. ` +
            "Kept separate; merge later in Settings if you prefer.",
        }),
      );
    }
  };

  for (const p of PACKS) {
    const { shown, more } = clampDimensionNames(p.dimensions.map((d) => d.name));
    // The clamped on-card summary keeps its 2-line shape; "+N more" becomes a
    // button that reveals the full list in a floating popover (no height change).
    const dims = el("p", { class: "pack-card__dims" }, shown.join(" · "));
    if (more > 0) {
      dims.append(document.createTextNode(" "));
      dims.append(moreControl(p, more));
    }
    const card = el(
      "div",
      {
        class: "pack-card pack-card--pick",
        attrs: { role: "button", tabindex: "0", "aria-pressed": "false" },
      },
      el(
        "div",
        { class: "pack-card__head" },
        el("span", { class: "pack-card__check", attrs: { "aria-hidden": "true" } }),
        el("span", { class: "pack-card__label", text: p.label }),
      ),
      el("p", { class: "pack-card__blurb", text: p.blurb }),
      dims,
    );
    const toggle = () => {
      const on = !selected.has(p.key);
      if (on) selected.add(p.key);
      else selected.delete(p.key);
      card.classList.toggle("is-on", on);
      card.setAttribute("aria-pressed", String(on));
      refresh();
    };
    card.addEventListener("click", toggle);
    card.addEventListener("keydown", (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === "Enter" || k === " ") {
        e.preventDefault();
        toggle();
      }
    });
    grid.append(card);
  }

  apply.addEventListener("click", () => {
    if (selected.size === 0) return;
    onApply(PACKS.filter((p) => selected.has(p.key)));
  });

  const actions = el("div", { class: "panel-actions pack-actions" }, apply);
  if (opts.onGeneric) {
    actions.append(
      el("button", {
        class: "btn btn--ghost",
        text: "Just use the generic set",
        on: { click: opts.onGeneric },
      }),
    );
  }

  refresh();
  return el("div", { class: "pack-chooser" }, grid, preview, actions);
}

// Propose-from-role: the user describes their role, the model proposes 6-8
// qualities each pre-mapped to a backbone key (or none), and the proposal is
// shown to EDIT and CONFIRM. It NEVER commits on its own: only the Confirm
// button calls onCommit. The model call is gated by the processing notice.
export function renderProposer(
  ctx: AppContext,
  onCommit: (dims: ProposedDimension[]) => Promise<void> | void,
): HTMLElement {
  const wrap = el("div", { class: "proposer" });
  const role = el("input", {
    class: "input",
    type: "text",
    placeholder: "e.g. I run corporate development at a mid-market industrials buyer",
  });
  const status = el("span", { class: "remaining" });
  const editor = el("div", { class: "proposer__editor" });
  editor.style.display = "none";

  const renderEditor = (dims: ProposedDimension[]) => {
    editor.replaceChildren();
    const rows: { name: HTMLInputElement; key: HTMLSelectElement; definition: HTMLInputElement }[] = [];
    for (const d of dims) {
      const name = el("input", { class: "input", type: "text", value: d.name });
      const key = canonicalSelect(d.canonical_key);
      const definition = el("input", { class: "input", type: "text", value: d.definition });
      rows.push({ name, key, definition });
      editor.append(el("div", { class: "proposer__row" }, name, key, definition));
    }
    const confirm = el("button", {
      class: "btn btn--primary",
      text: "Use these qualities",
      on: {
        click: async () => {
          const chosen = rows
            .map((r) => ({
              name: r.name.value.trim(),
              definition: r.definition.value.trim(),
              canonical_key: readCanonical(r.key),
            }))
            .filter((r) => r.name);
          if (chosen.length === 0) {
            status.textContent = "Keep at least one quality.";
            status.classList.add("is-over");
            return;
          }
          await onCommit(chosen);
        },
      },
    });
    editor.append(
      el("p", {
        class: "lede",
        text:
          "Edit any name, mapping or definition, then confirm. The mapping is " +
          "optional plumbing; nothing is saved until you confirm.",
      }),
      el("div", { class: "panel-actions" }, confirm),
    );
    editor.style.display = "block";
  };

  const propose = el("button", {
    class: "btn btn--ghost",
    text: "Propose qualities",
    on: {
      click: async () => {
        const text = role.value.trim();
        if (!text) {
          status.textContent = "Describe your role first.";
          status.classList.add("is-over");
          return;
        }
        if (!(await requireProcessingConsent(ctx))) return;
        propose.disabled = true;
        status.classList.remove("is-over");
        status.textContent = "Proposing...";
        try {
          const dims = await proposeDimensions(ctx.provider, text);
          if (dims.length === 0) {
            status.textContent = "The model did not return usable qualities. Try rephrasing.";
            status.classList.add("is-over");
            return;
          }
          status.textContent = "";
          renderEditor(dims);
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Could not reach the model.";
          status.classList.add("is-over");
        } finally {
          propose.disabled = false;
        }
      },
    },
  });

  wrap.append(
    el("p", {
      class: "lede",
      text:
        "Or describe your role and let the model propose a set, each mapped to a " +
        "shared quality. You edit and confirm before anything is saved; the role " +
        "text is sent to the model.",
    }),
    el("div", { class: "proposer__ask" }, role, propose),
    status,
    editor,
  );
  return wrap;
}

// A purpose filter chip row, shown only when more than one purpose is present
// (otherwise there is nothing to narrow). "All" plus one chip per purpose;
// onSelect gets the purpose key, or null for "All". Keeps a large multi-purpose
// set manageable across the people, person and assemble views.
export function renderPurposeFilter(
  present: { key: string; label: string; count: number }[],
  onSelect: (key: string | null) => void,
): HTMLElement | null {
  if (present.length < 2) return null;
  const row = el("div", { class: "purpose-filter" });
  const chips: { key: string | null; el: HTMLElement }[] = [];
  const paint = (active: string | null) =>
    chips.forEach((c) => c.el.classList.toggle("is-on", c.key === active));
  const add = (key: string | null, text: string) => {
    const chip = el("button", {
      class: "chip purpose-chip",
      text,
      on: {
        click: () => {
          paint(key);
          onSelect(key);
        },
      },
    });
    chips.push({ key, el: chip });
    row.append(chip);
  };
  add(null, "All");
  for (const p of present) add(p.key, `${p.label} (${p.count})`);
  paint(null);
  return row;
}

// Map-on-create / map-on-edit control: a canonical picklist with an optional
// "suggest" that calls the model for the nearest key and PRESELECTS it (never
// auto-commits; committing is the caller's Add/Save). Returns the select plus
// the row element to mount.
export function renderCanonicalField(
  ctx: AppContext,
  selected: string | null,
  read: () => { name: string; definition: string },
): { select: HTMLSelectElement; row: HTMLElement } {
  const select = canonicalSelect(selected);
  const reason = el("span", { class: "remaining canonical-field__reason" });
  const suggest = el("button", {
    class: "btn btn--ghost btn--small",
    type: "button",
    text: "Suggest",
    on: {
      click: async () => {
        const { name, definition } = read();
        if (!name) {
          reason.textContent = "Name the quality first.";
          reason.classList.add("is-over");
          return;
        }
        if (!(await requireProcessingConsent(ctx))) return;
        suggest.disabled = true;
        reason.classList.remove("is-over");
        reason.textContent = "Thinking...";
        try {
          const s = await suggestCanonical(ctx.provider, name, definition);
          // Preselect only; the user still has to Add/Save to commit.
          select.value = s.canonical_key ?? "";
          reason.textContent = s.reason ? `Suggested: ${s.reason}` : "No clear match; left as none.";
        } catch (e) {
          reason.textContent = e instanceof Error ? e.message : "Could not reach the model.";
          reason.classList.add("is-over");
        } finally {
          suggest.disabled = false;
        }
      },
    },
  });
  const row = el(
    "div",
    { class: "canonical-field" },
    el("span", { class: "label", text: "Maps to" }),
    select,
    suggest,
    reason,
  );
  return { select, row };
}
